import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../utils/api';

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactSupportModal({ isOpen, onClose }: ContactSupportModalProps) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  // Concerns section
  const [concernsExpanded, setConcernsExpanded] = useState(true);
  const [concernSubject, setConcernSubject] = useState('');
  const [concernDescription, setConcernDescription] = useState('');

  // Improvements section
  const [improvementsExpanded, setImprovementsExpanded] = useState(true);
  const [improvements, setImprovements] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Pre-fill user info when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setName(user.fullName || '');
      setEmail(user.email || '');
    }
  }, [isOpen, user]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setConcernSubject('');
        setConcernDescription('');
        setImprovements('');
        setConcernsExpanded(true);
        setImprovementsExpanded(true);
        setError('');
        setSuccess(false);
      }, 300); // Wait for modal close animation
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate that at least one field is filled
    if (!concernSubject && !concernDescription && !improvements) {
      setError('Please fill in at least one field to submit your request.');
      return;
    }

    setIsLoading(true);

    try {
      await apiRequest('/contact-support', {
        method: 'POST',
        body: JSON.stringify({
          name,
          email,
          concern_subject: concernSubject || null,
          concern_description: concernDescription || null,
          improvements: improvements || null,
        }),
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000); // Close after 2 seconds
    } catch (error: any) {
      setError(error.message || 'Failed to send support request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-dark border bg-background border-border-light rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-white text-xl font-bold">Contact Support</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success Message */}
        {success ? (
          <div className="p-6">
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-white text-lg font-bold mb-2">Request Sent!</h3>
              <p className="text-text-secondary">
                Thank you for contacting us. We've sent you a confirmation email and will get back to you shortly.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Description */}
            <div className="p-6 border-b border-border-light">
              <p className="text-text-secondary text-sm">
                Use this form to raise any concerns you have and/or suggest improvements or request new features.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Name and Email Row */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="form-input flex w-full resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-12 placeholder:text-text-secondary px-4 text-base font-normal leading-normal"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input flex w-full resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-12 placeholder:text-text-secondary px-4 text-base font-normal leading-normal"
                  />
                </div>
              </div>

              {/* Concerns Section */}
              <div className="mb-4 border border-border-light rounded">
                <button
                  type="button"
                  onClick={() => setConcernsExpanded(!concernsExpanded)}
                  className="w-full flex items-center justify-between p-4 text-white hover:bg-surface-light transition-colors"
                >
                  <span className="font-medium">Raise Concerns</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${concernsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {concernsExpanded && (
                  <div className="p-4 border-t border-border-light space-y-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={concernSubject}
                        onChange={(e) => setConcernSubject(e.target.value)}
                        placeholder="Brief description of the issue"
                        className="form-input flex w-full resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light h-12 placeholder:text-text-secondary px-4 text-base font-normal leading-normal"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Description
                      </label>
                      <textarea
                        value={concernDescription}
                        onChange={(e) => setConcernDescription(e.target.value)}
                        placeholder="Please provide detailed information about the problem you're experiencing..."
                        rows={4}
                        className="form-input flex w-full resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light placeholder:text-text-secondary p-4 text-base font-normal leading-normal"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Improvements/Features Section */}
              <div className="mb-6 border border-border-light rounded">
                <button
                  type="button"
                  onClick={() => setImprovementsExpanded(!improvementsExpanded)}
                  className="w-full flex items-center justify-between p-4 text-white hover:bg-surface-light transition-colors"
                >
                  <span className="font-medium">Improvements / Features</span>
                  <svg
                    className={`w-5 h-5 transition-transform ${improvementsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {improvementsExpanded && (
                  <div className="p-4 border-t border-border-light">
                    <label className="block text-white text-sm font-medium mb-2">
                      Suggestions
                    </label>
                    <textarea
                      value={improvements}
                      onChange={(e) => setImprovements(e.target.value)}
                      placeholder="Tell us about your improvement ideas or feature requests..."
                      rows={4}
                      className="form-input flex w-full resize-none overflow-hidden rounded text-white focus:outline-0 focus:ring-0 border border-border-light bg-input-bg focus:border-border-light placeholder:text-text-secondary p-4 text-base font-normal leading-normal"
                    />
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-6 py-2.5 rounded text-white border border-border-light hover:bg-surface-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2.5 rounded bg-primary hover:bg-primary/90 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
